import React, { useCallback } from 'react';
import numeral from 'numeral';
import { isMandatoryField, getFieldCode } from '../utils/mandatoryFields';
import { isReadOnlyField } from '../utils/readOnlyFields';
import { ValidationIndicator } from './ValidationIndicator';
import { getTextBlockPreview } from '../utils/reportNoteOrder';
import { SSM_CURRENCIES } from '../utils/ssmCurrencies';

interface FormFieldProps {
  field: any;
  companyInfo: any;
  formData: Record<string, any>;
  onFieldChange: (name: string, contextRef: string, value: string, qname?: string) => void;
  activeEditor?: string | null;
  onEditorToggle?: (fieldId: string | null) => void;
  // hasError/errorMessage removed - validation now handled via DOM manipulation in useValidation.ts
}

/** Opens method-change modals from EditFilings (presentation / conversion pipeline). */
const METHOD_CHANGE_BTN_TEXT = 'Change method';

const METHOD_PRESENTATION_CHANGE: Record<string, { eventName: string }> = {
  'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss': {
    eventName: 'openExpensesMethodChangeModal'
  },
  'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity': {
    eventName: 'openEquityMethodChangeModal'
  },
  'ssmt:MethodUsedForPreparingStatementOfFinancialPosition': {
    eventName: 'openSofpMethodChangeModal'
  },
  'ssmt:MethodUsedForPreparingStatementOfCashFlows': {
    eventName: 'openCashFlowsMethodChangeModal'
  }
};

/**
 * FormField - Renders individual form fields (PURE inputs, no error UI)
 * Error indicators are added via DOM manipulation in useValidation.ts (no React re-renders)
 * 
 * CRITICAL: Memoized to prevent re-renders when parent state changes
 * This prevents React DOM reconciliation conflicts with manual DOM manipulation in useValidation
 */
const FormFieldComponent: React.FC<FormFieldProps> = ({ 
  field, 
  companyInfo, 
  formData, 
  onFieldChange, 
  activeEditor, 
  onEditorToggle
}) => {
  const isReadOnly = isReadOnlyField(field.qname);
  
  // Helper to format date strings
  const formatDateForContext = (dateStr: string): string => {
    if (/^\d{8}$/.test(dateStr)) {
      return dateStr;
    }
    return dateStr.replace(/-/g, '');
  };

  // Helper function to get period context
  const getPeriodContext = (field: any, companyInfo: any, suffix: string = '') => {
    const period = companyInfo.currentPeriod || companyInfo.reporting_period;
    
    if (!period) {
      return `context_${suffix || 'default'}`;
    }

    const formattedStart = formatDateForContext(period.start);
    const formattedEnd = formatDateForContext(period.end);

    if (field.properties?.period_type === 'duration') {
      return `fromto_${formattedStart}_${formattedEnd}${suffix}`;
    } else {
      return `asof_${formattedEnd}${suffix}`;
    }
  };

  const contextRef = getPeriodContext(field, companyInfo);
  const fieldKey = `${field.qname}_${contextRef}`;
  const initialValue = formData[fieldKey] || '';

  const handleChange = useCallback((value: string) => {
    onFieldChange(field.name, contextRef, value, field.qname);
  }, [field.name, contextRef, onFieldChange, field.qname]);

  // Text fields (modal drawer like 1.0)
  if (field.properties?.data_type === 'text') {
    const fieldId = `${field.name}_${contextRef}`;
    // Note: isEditorActive removed - button always passes fieldId, handleEditorToggle decides
    
    const previewText = getTextBlockPreview(initialValue);
    
    return (
      <div className="form-group row text-block-wrapper" data-field-id={field.name}>
        <div className="col-2">
          <label className="form-label">
            {field.standard_label}
            {isMandatoryField(field.qname) && <span style={{ color: 'red' }}> *</span>}
            {getFieldCode(field.qname) && <span style={{ color: 'red', fontSize: '1em' }}> ({getFieldCode(field.qname)})</span>}
          </label>
        </div>
        
        <div className="col-2">
          <div className="text-block-input-group">
            {/* 🔒 CRITICAL FIX: Empty container - React NEVER renders children
                JavaScript manages content via textContent in EditFilings.tsx
                This prevents React/DOM ownership conflict → removeChild crashes */}
            <div 
              className="text-preview"
              ref={(el) => {
                if (el) el.textContent = previewText;
              }}
            />
            <button 
              type="button" 
              className="edit-text-block"
              onClick={() => {
                // Always pass fieldId - let handleEditorToggle decide if modal should open
                // This fixes stale activeEditor prop issue when FormField doesn't re-render
                if (onEditorToggle) {
                  onEditorToggle(fieldId);
                }
              }}
            >
              Edit
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
          </div>
          
          <textarea
            className="form-control hidden"
            name={field.name}
            data-qname={field.qname}
            data-period-type={field.properties?.period_type}
            data-type={field.properties?.data_type}
            data-balance={field.properties?.balance}
            data-context-ref={contextRef}
            defaultValue={initialValue}
            rows={3}
          />
        </div>
      </div>
    );
  }

  // Enumeration fields (dropdown)
  if (field.properties?.data_type?.type === 'enumeration') {
    const methodChange = METHOD_PRESENTATION_CHANGE[field.qname];
    const readOnlySelectStyle = isReadOnly
      ? { pointerEvents: 'none' as const, backgroundColor: '#f5f5f5', color: '#6c757d', cursor: 'not-allowed' as const }
      : {};

    return (
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'nowrap'
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <select
              className="form-control select select-sm"
              name={field.name}
              data-qname={field.qname}
              data-period-type={field.properties?.period_type}
              data-type={field.properties?.data_type?.type}
              data-balance={field.properties?.balance}
              data-context-ref={contextRef}
              defaultValue={initialValue}
              onChange={(e) => handleChange(e.target.value)}
              data-readonly={isReadOnly ? 'true' : undefined}
              tabIndex={isReadOnly ? -1 : undefined}
              style={Object.keys(readOnlySelectStyle).length ? readOnlySelectStyle : undefined}
            >
              <option value="">Please Select</option>
              {field.properties?.data_type?.values?.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
          </div>

          {methodChange && (
            <button
              type="button"
              className="method-presentation-change-btn"
              title={METHOD_CHANGE_BTN_TEXT}
              aria-label={METHOD_CHANGE_BTN_TEXT}
              onClick={(e) => {
                e.preventDefault();
                document.dispatchEvent(new CustomEvent(methodChange.eventName));
              }}
            >
              <svg
                className="method-presentation-change-btn__icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="method-presentation-change-btn__text">{METHOD_CHANGE_BTN_TEXT}</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Date fields
  if (field.properties?.data_type === 'date') {
    return (
      <div>
        <div style={{ position: 'relative', width: '100%' }}>
          <input 
            type="date" 
            className="form-control"
            name={field.name}
            data-qname={field.qname} 
            data-period-type={field.properties?.period_type} 
            data-type={field.properties?.data_type} 
            data-balance={field.properties?.balance} 
            data-context-ref={contextRef}
            defaultValue={initialValue}
            onChange={(e) => handleChange(e.target.value)}
            data-readonly={isReadOnly ? 'true' : undefined}
            tabIndex={isReadOnly ? -1 : undefined}
            readOnly={isReadOnly}
            style={isReadOnly ? { pointerEvents: 'none', backgroundColor: '#f5f5f5', color: '#6c757d', cursor: 'not-allowed' } : {}}
          />
        <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
        </div>

        {field.qname === 'ssmt-dei:CompanyPreviousFinancialYearEndDate' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                // Dispatch event to EditFilings to open year-end modal
                document.dispatchEvent(new CustomEvent('openYearEndModal'));
              }} 
              style={{ color: '#007bff', textDecoration: 'underline', fontSize: '0.9em' }}
            >
              Modify Year End
            </a>
          </div>
        )}
      </div>
    );
  }

  // Decimal/Monetary fields
  if (field.properties?.data_type === 'decimal' || field.properties?.data_type === 'monetary') {
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <input 
          type="number" 
          className="form-control"
          name={field.name}
          data-qname={field.qname} 
          data-period-type={field.properties?.period_type} 
          data-type={field.properties?.data_type} 
          data-balance={field.properties?.balance} 
          data-context-ref={contextRef}
          step="0.01"
          defaultValue={initialValue}
          onChange={(e) => handleChange(e.target.value)}
          data-readonly={isReadOnly ? 'true' : undefined}
          tabIndex={isReadOnly ? -1 : undefined}
          readOnly={isReadOnly}
          style={isReadOnly ? { backgroundColor: '#f5f5f5', color: '#6c757d', cursor: 'not-allowed' } : {}}
        />
        <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
      </div>
    );
  }

  // Audit Exemption Category
  if (field.qname === 'ssmt-dei:AuditExemptionCategory') {
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <select 
          className="form-control select select-sm"
          name={field.name}
          data-qname={field.qname} 
          data-period-type={field.properties?.period_type} 
          data-type={field.properties?.data_type?.type} 
          data-balance={field.properties?.balance} 
          data-context-ref={contextRef}
          defaultValue={initialValue}
          onChange={(e) => handleChange(e.target.value)}
          data-readonly={isReadOnly ? 'true' : undefined}
          tabIndex={isReadOnly ? -1 : undefined}
          style={isReadOnly ? { pointerEvents: 'none', backgroundColor: '#f5f5f5', color: '#6c757d', cursor: 'not-allowed' } : {}}
        >
          <option value="">Please Select</option>
          <option value="Threshold-Qualified company">Threshold-Qualified company</option>
          <option value="Zero-revenue company">Zero-revenue company</option>
          <option value="Dormant company">Dormant company</option>
        </select>
        <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
      </div>
    );
  }

  // Currency dropdown - full SSM list, native scrollable select
  if (field.qname === 'ssmt-dei:DescriptionOfFunctionalCurrency') {
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <select
          className="form-control select select-sm"
          name={field.name}
          data-qname={field.qname}
          data-period-type={field.properties?.period_type}
          data-type={field.properties?.data_type?.type}
          data-balance={field.properties?.balance}
          data-context-ref={contextRef}
          defaultValue={initialValue}
          onChange={(e) => handleChange(e.target.value)}
          data-readonly={isReadOnly ? 'true' : undefined}
          tabIndex={isReadOnly ? -1 : undefined}
          style={isReadOnly ? { pointerEvents: 'none', backgroundColor: '#f5f5f5', color: '#6c757d', cursor: 'not-allowed' } : {}}
        >
          <option value="">Please Select</option>
          {SSM_CURRENCIES.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {currency.description}
            </option>
          ))}
        </select>
        <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
      </div>
    );
  }

  // ID Type dropdown fields
  const idTypeFields = [
    'ssmt:TypeOfIdentificationOfFirstDirectorWhoSignedDirectorsReport',
    'ssmt:TypeOfIdentificationOfSecondDirectorWhoSignedDirectorsReport',
    'ssmt:TypeOfIdentificationOfThirdDirectorWhoSignedDirectorsReport',
    'ssmt:TypeOfIdentificationOfFourthDirectorWhoSignedDirectorsReport',
    'ssmt:TypeOfIdentificationOfFifthDirectorWhoSignedDirectorsReport',
    'ssmt:TypeOfIdentificationOfOtherPersonPrimarilyResponsibleForFinancialStatementsOfCompany',
    'ssmt:TypeOfIdentificationOfFirstDirectorWhoSignedStatementByDirectors',
    'ssmt:TypeOfIdentificationNumberOfSecondDirectorWhoSignedStatementByDirectors',
    'ssmt:TypeOfIdentificationOfDirectorWhoSignedCertificateOfExemptPrivateCompany',
    'ssmt:TypeOfIdentificationOfCompanySecretaryWhoSignedCertificateOfExemptPrivateCompany',
    'ssmt:TypeOfIdentificationOfPersonWhoSignedStatutoryDeclaration',
    'ssmt:TypeOfIdentification',
    'ssmt:TypeOfIdentificationOfThirdDirectorWhoSignedStatementByDirectors',
    'ssmt:TypeOfIdentificationOfFourthDirectorWhoSignedStatementByDirectors',
    'ssmt:TypeOfIdentificationOfFifthDirectorWhoSignedStatementByDirectors',
    'ssmt:TypeOfIdentificationOfShareholder'
  ];

  if (idTypeFields.includes(field.qname)) {
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <select 
          className="form-control select select-sm"
          name={field.name}
          data-qname={field.qname} 
          data-period-type={field.properties?.period_type} 
          data-type={field.properties?.data_type?.type} 
          data-balance={field.properties?.balance} 
          data-context-ref={contextRef}
          defaultValue={initialValue}
          onChange={(e) => handleChange(e.target.value)}
          data-readonly={isReadOnly ? 'true' : undefined}
          tabIndex={isReadOnly ? -1 : undefined}
          style={isReadOnly ? { pointerEvents: 'none', backgroundColor: '#f5f5f5', color: '#6c757d', cursor: 'not-allowed' } : {}}
        >
          <option value="">Please Select</option>
          <option value="MyKad">MyKad</option>
          <option value="MyPR">MyPR</option>
          <option value="Passport number">Passport number</option>
          <option value="Old IC number">Old IC number</option>
          <option value="Company Registration No.">Company Registration No.</option>
          <option value="LLP Registration No.">LLP Registration No.</option>
        </select>
        <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
      </div>
    );
  }

  // Default: text input
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input 
        type="text" 
        className="form-control"
        name={field.name}
        data-qname={field.qname} 
        data-period-type={field.properties?.period_type} 
        data-type={field.properties?.data_type} 
        data-balance={field.properties?.balance} 
        data-context-ref={contextRef}
        placeholder={`Enter ${field.standard_label}`}
        defaultValue={initialValue}
        onChange={(e) => handleChange(e.target.value)}
        data-readonly={isReadOnly ? 'true' : undefined}
        tabIndex={isReadOnly ? -1 : undefined}
        readOnly={isReadOnly}
        style={isReadOnly ? { backgroundColor: '#f5f5f5', color: '#6c757d', cursor: 'not-allowed' } : {}}
      />
    <ValidationIndicator fieldName={field.name} contextRef={contextRef} />
    </div>
  );
};

// Export memoized version to prevent unnecessary re-renders
// This is CRITICAL for preventing React reconciliation conflicts with useValidation DOM manipulation
export const FormField = React.memo(FormFieldComponent);